// src/pages/auth/AuthCallbackPage.jsx
import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { provisionUser, getMyProfile } from '../../api/user-service/userService';
import { getRoleFromToken } from '../../utils/jwtUtils';

/**
 * Auth Callback Page
 * Handles Auth0 callback after login/signup
 * Provisions new users and loads existing user profiles
 */
export default function AuthCallbackPage() {
  const { 
    isAuthenticated, 
    isLoading: auth0Loading, 
    user,
    getAccessTokenSilently,
  } = useAuth0();
  
  const navigate = useNavigate();
  const { setUserProfile, setRole, isProvisioned } = useAuthStore();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Initializing...');

  useEffect(() => {
    const handleCallback = async () => {
      // Wait for Auth0 to finish loading
      if (auth0Loading) {
        setStatus('Authenticating with Auth0...');
        return;
      }

      // Check if user is authenticated
      if (!isAuthenticated || !user) {
        setStatus('Authentication failed. Redirecting...');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      try {
        setStatus('Getting access token...');
        
        // Get access token
        const token = await getAccessTokenSilently();
        const role = getRoleFromToken(token);
        setRole(role);

        // Try to get existing user profile
        setStatus('Loading your profile...');
        
        try {
          const profileResponse = await getMyProfile();
          
          if (profileResponse.success && profileResponse.data) {
            // User already exists in backend
            setUserProfile(profileResponse.data);
            setStatus('Welcome back! Redirecting...');
            
            setTimeout(() => {
              navigate('/');
            }, 1000);
            return;
          }
        } catch (profileError) {
          // User doesn't exist yet, need to provision
          if (profileError.response?.status === 404) {
            setStatus('Setting up your account...');
            
            // Get role from appState if available
            const appState = window.history.state?.usr;
            const roleCode = appState?.roleCode;
            
            // Provision user
            const provisionResponse = await provisionUser(
              roleCode || null
            );
            
            if (provisionResponse.success && provisionResponse.data) {
              setUserProfile(provisionResponse.data);
              setStatus('Account created successfully! Redirecting...');
              
              setTimeout(() => {
                navigate('/');
              }, 1000);
              return;
            } else {
              throw new Error('Failed to create user profile');
            }
          } else {
            throw profileError;
          }
        }
      } catch (err) {
        console.error('Callback error:', err);
        setError(err.message || 'Failed to complete authentication');
        setStatus('Error occurred. Please try again.');
        
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    };

    handleCallback();
  }, [
    isAuthenticated, 
    auth0Loading, 
    user, 
    getAccessTokenSilently, 
    navigate, 
    setUserProfile, 
    setRole,
    isProvisioned,
  ]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        background: 'white',
        borderRadius: '16px',
        padding: '3rem',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        textAlign: 'center',
      }}>
        {!error ? (
          <>
            <div style={{
              fontSize: '4rem',
              marginBottom: '1.5rem',
            }}>
              🌱
            </div>
            
            <h2 style={{
              fontSize: '1.8rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: '#333',
            }}>
              Welcome to FarmCiti!
            </h2>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem',
            }}>
              <div
                style={{
                  width: '50px',
                  height: '50px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #4CAF50',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              
              <p style={{
                fontSize: '1.1rem',
                color: '#666',
              }}>
                {status}
              </p>
            </div>
          </>
        ) : (
          <>
            <div style={{
              fontSize: '4rem',
              marginBottom: '1.5rem',
            }}>
              ⚠️
            </div>
            
            <h2 style={{
              fontSize: '1.8rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: '#d32f2f',
            }}>
              Oops! Something went wrong
            </h2>
            
            <p style={{
              fontSize: '1rem',
              color: '#666',
              marginBottom: '1.5rem',
            }}>
              {error}
            </p>
            
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                background: '#4CAF50',
                color: 'white',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              Go to Home
            </button>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
